import { Cache } from '@app/utils/cache/cache';
import { StringMap } from '@app/utils/string-map';
import { BehaviorSubject, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import Timeout = NodeJS.Timeout;

class _InMemoryCache implements Cache {
  private cache$: BehaviorSubject<StringMap<any>> = new BehaviorSubject({});

  private cache: StringMap<any> = {};
  private timeouts: StringMap<Timeout> = {};

  constructor() {}

  public clear(): void {
    this.cache = {};

    this.clearAllTimeouts();
    this.update();
  }

  public evict(key: string): void {
    delete this.cache[key];

    this.clearTimeout(key);
    this.update();
  }

  public evictAllWithPrefix(prefix: string) {
    Object.keys(this.cache)
      .filter((key) => key.startsWith(prefix))
      .forEach((key) => {
        delete this.cache[key];

        this.clearTimeout(key);
      });

    this.update();
  }

  public get<T>(key: string): T | undefined {
    return this.cache[key];
  }

  public observe<T>(): Observable<StringMap<any>>;
  public observe<T>(key: string): Observable<T | undefined>;
  public observe<T>(key?: string): Observable<StringMap<any> | T | undefined> {
    let observedValue$: Observable<StringMap<any> | T | undefined>;

    if (key != null) {
      observedValue$ = this.cache$.pipe(map((value) => value[key]));
    } else {
      observedValue$ = this.cache$;
    }

    return observedValue$;
  }

  public set<T>(key: string, value: T, ttl: number): T {
    this.cache[key] = value;

    this.update();
    this.waitForTimeout(key, ttl);

    return value;
  }

  private clearAllTimeouts(): void {
    Object.values(this.timeouts).forEach((timeout) => {
      clearTimeout(timeout);
    });

    this.timeouts = {};
  }

  private clearTimeout(key: string): void {
    if (this.timeouts[key] != null) {
      clearTimeout(this.timeouts[key]);

      delete this.timeouts[key];
    }
  }

  private update(): void {
    this.cache$.next(this.cache);
  }

  private waitForTimeout(key: string, ttl: number): void {
    this.clearTimeout(key);

    this.timeouts[key] = setTimeout(() => {
      this.evict(key);
    }, ttl);
  }
}

export const InMemoryCache = new _InMemoryCache();
